import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { formatCents } from '@shared/money';
import styles from './RecurringPage.module.css';

/** A recurring service rule as returned by GET /api/v1/recurring. */
interface RecurringRule {
  id: string;
  name: string;
  status: string;
  customerId: string;
  propertyId: string;
  serviceId: string | null;
  startDate: string;
  endDate: string | null;
  recurrenceType: string;
  intervalCount: number;
  dayOfWeek: number | null;
  priceCents: number;
  estimatedDurationMinutes: number;
  notes: string | null;
  nextGenerationDate: string | null;
}

/** The server response after POSTing to /generate: the jobs it created. */
interface GenerateResult {
  created: number;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; rules: RecurringRule[] };

/**
 * dayOfWeek is 0=Sunday through 6=Saturday, matching the database and the
 * recurrence engine. Index 0 IS Sunday — an array starting at Monday would
 * render every rule one day off.
 */
const DAY_NAMES: readonly string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Format a rule's schedule into plain English for the owner.
 *
 * - WEEKLY with dayOfWeek 0 -> "Every Sunday"
 * - WEEKLY with dayOfWeek 6 -> "Every Saturday"
 * - BIWEEKLY -> "Every 2 weeks on Wednesday"
 * - MONTHLY -> "Monthly"
 * - CUSTOM_DAYS with intervalCount 3 -> "Every 3 days"
 *
 * No raw enum value is ever rendered.
 */
export function formatSchedule(rule: RecurringRule): string {
  const { recurrenceType, intervalCount, dayOfWeek } = rule;

  switch (recurrenceType) {
    case 'WEEKLY': {
      const day =
        dayOfWeek !== null && dayOfWeek >= 0 && dayOfWeek < DAY_NAMES.length
          ? DAY_NAMES[dayOfWeek]
          : 'day';
      return `Every ${day}`;
    }
    case 'BIWEEKLY': {
      const day =
        dayOfWeek !== null && dayOfWeek >= 0 && dayOfWeek < DAY_NAMES.length
          ? DAY_NAMES[dayOfWeek]
          : 'day';
      return `Every ${intervalCount} weeks on ${day}`;
    }
    case 'MONTHLY': {
      return 'Monthly';
    }
    case 'CUSTOM_DAYS': {
      return `Every ${intervalCount} days`;
    }
    default: {
      return 'Custom schedule';
    }
  }
}

/**
 * The Recurring services screen.
 *
 * Lists the owner's recurring service rules (e.g. "Weekly mow & edge"), each
 * with a human-readable schedule, price, and next generation date. Each rule
 * has a "Generate jobs" button that POSTs to /api/v1/recurring/:id/generate.
 *
 * - GET /api/v1/recurring lists the org's rules.
 * - POST /api/v1/recurring/:id/generate creates the jobs due for that rule.
 *
 * The generate button is guarded against double-fire with a REF, not with
 * pending state: two clicks in one tick both run before React re-renders, so
 * both closures read pending === false and `disabled` has not been applied
 * yet. A ref updates synchronously. Generating twice is a request to create
 * real jobs, so exactly-once matters here.
 */
export function RecurringPage() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  /** id of the rule currently generating, or null. */
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  /** Error message for a failed generate, or null. */
  const [generateError, setGenerateError] = useState<string | null>(null);
  /** Success message for a completed generate, or null. */
  const [generateSuccess, setGenerateSuccess] = useState<string | null>(null);

  /**
   * A ref per rule id is not needed — only one generate can be in flight at a
   * time, so a single ref tracks whether ANY generate is happening. This is
   * the exactly-once guard: it updates synchronously, so a second click in the
   * same tick sees the first one's write before React re-renders.
   */
  const generatingRef = useRef(false);

  const fetchRules = useCallback(async () => {
    setState({ kind: 'loading' });
    setGenerateError(null);
    setGenerateSuccess(null);
    try {
      const data = await apiGet<RecurringRule[]>('/api/v1/recurring');
      setState({ kind: 'loaded', rules: data });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiClientError ? err.message : 'Could not load recurring services.',
      });
    }
  }, []);

  useEffect(() => {
    void fetchRules();
  }, [fetchRules]);

  const handleGenerate = useCallback(
    async (rule: RecurringRule) => {
      // The ref is the exactly-once guard. Two clicks in one tick both call
      // this handler before React re-renders; the first sets the ref to true
      // synchronously, so the second returns immediately.
      if (generatingRef.current) return;
      generatingRef.current = true;
      setGeneratingId(rule.id);
      setGenerateError(null);
      setGenerateSuccess(null);
      try {
        const result = await apiPost<GenerateResult>(`/api/v1/recurring/${rule.id}/generate`, {});
        setGenerateSuccess(`${result.created} job${result.created === 1 ? '' : 's'} created.`);
        // Refresh the list so nextGenerationDate updates.
        void fetchRules();
      } catch (err) {
        setGenerateError(
          err instanceof ApiClientError ? err.message : 'Could not generate jobs. Please try again.'
        );
      } finally {
        generatingRef.current = false;
        setGeneratingId(null);
      }
    },
    [fetchRules]
  );

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading recurring services…
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchRules()}>
          Retry
        </button>
      </main>
    );
  }

  const { rules } = state;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Recurring services</h1>
        <Link
          to="/jobs/new?repeats=1"
          className={styles.newButton}
          aria-label="New recurring service"
        >
          New recurring service
        </Link>
      </header>

      {generateSuccess && (
        <p className={styles.success} role="status">
          {generateSuccess}
        </p>
      )}
      {generateError && (
        <p className={styles.error} role="alert">
          {generateError}
        </p>
      )}

      {rules.length === 0 ? (
        <section className={styles.empty} aria-label="Empty state">
          <p className={styles.emptyText}>
            No recurring services yet. A recurring service is a rule that automatically creates jobs
            on a schedule — for example, mowing a customer's property every week. When you generate
            jobs, the system creates the work due for that rule and advances the next generation
            date.
          </p>
          <Link
            to="/jobs/new?repeats=1"
            className={styles.newButton}
            aria-label="New recurring service"
          >
            New recurring service
          </Link>
        </section>
      ) : (
        <ul className={styles.ruleList}>
          {rules.map((rule) => {
            const isGenerating = generatingId === rule.id;
            const priceDisplay = formatCents(rule.priceCents);
            const nextDate = rule.nextGenerationDate ?? 'not scheduled';
            return (
              <li key={rule.id} className={styles.ruleRow}>
                <div className={styles.ruleInfo}>
                  <span className={styles.fieldLabel}>Name</span>
                  <Link
                    to={`/recurring/${rule.id}`}
                    className={styles.ruleName}
                    aria-label={`View ${rule.name}`}
                  >
                    {rule.name}
                  </Link>
                  <span className={styles.fieldLabel}>Status</span>
                  <span className={styles.ruleStatus}>{rule.status}</span>
                  <span className={styles.fieldLabel}>Schedule</span>
                  <span className={styles.ruleSchedule}>{formatSchedule(rule)}</span>
                  <span className={styles.fieldLabel}>Price</span>
                  <span className={styles.rulePrice}>{priceDisplay}</span>
                  <span className={styles.fieldLabel}>Next generation</span>
                  <span className={styles.ruleNextDate}>{nextDate}</span>
                </div>
                <button
                  type="button"
                  className={styles.generateButton}
                  onClick={() => void handleGenerate(rule)}
                  disabled={isGenerating}
                  aria-busy={isGenerating}
                  aria-label={`Generate jobs for ${rule.name}`}
                >
                  {isGenerating ? 'Generating…' : 'Generate jobs'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
